import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { QueryKeys } from '../constants';
import { MissingIdError, useFetch } from './utils';

export function useUpdateRole() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, organizationId, data }) => {
      if (!organizationId) {
        throw new MissingIdError('organizationId');
      }
      return fetcher({
        path: `v1/onboarding/users/${userId}/role/update/`,
        method: 'PUT',
        data: JSON.stringify(data),
      });
    },
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries([QueryKeys.USERS, { organizationId }]);
    },
    onError: () => {
      message.error('Something went wrong updating user type');
    },
  });
}

// Creates a user in the caller's own organization. The response carries a
// one-time temporary password, so callers must surface it rather than discard
// it - it is not retrievable afterwards.
export function useCreateUser() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }) =>
      fetcher({
        path: 'v1/onboarding/users/create/',
        method: 'POST',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries([QueryKeys.USERS, { organizationId }]);
    },
  });
}

export function useDeleteUser() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, organizationId }) => {
      if (!organizationId) {
        throw new MissingIdError('organizationId');
      }
      return fetcher({
        path: `v1/onboarding/users/${userId}/delete/`,
        method: 'DELETE',
      });
    },
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries([QueryKeys.USERS, { organizationId }]);
    },
    onError: () => {
      message.error('Something went wrong deleting user');
    },
  });
}

export function useUpdateOrganization() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }) =>
      fetcher({
        path: 'v1/onboarding/organizations/update/',
        method: 'PUT',
        data: JSON.stringify(data),
      }),
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.ORGANIZATIONS]);
      queryClient.invalidateQueries([QueryKeys.ORGANIZATION]);
    },
    onError: () => {
      message.error('Something went wrong updating organization');
    },
  });
}

export function useVerifyEmail() {
  const fetcher = useFetch();
  return useMutation({
    mutationFn: () => fetcher({ path: 'v1/onboarding/users/verify/', method: 'POST' }),
  });
}

export function useUsers({ organizationId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.USERS, { organizationId }],
    queryFn: () => fetcher({ path: `v1/onboarding/organizations/${organizationId}/users/` }),
    ...options,
  });
}

export function useOrganization(options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.ORGANIZATION],
    queryFn: () => fetcher({ path: 'v1/onboarding/organizations/me/' }),
    ...options,
  });
}

export function useOrganizations() {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.ORGANIZATIONS],
    queryFn: () => fetcher({ path: 'v1/onboarding/organizations/' }),
  });
}

export function useCreateUpdateOrganization() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }) =>
      fetcher({
        path: 'v1/onboarding/organizations/update/',
        method: 'PUT',
        data: JSON.stringify(data),
      }),
    onSettled: () => queryClient.invalidateQueries([QueryKeys.ORGANIZATIONS]),
    onError: () => {
      message.error('Something went wrong creating or updating organization');
    },
  });
}

export function useOrganizationSurveys({ organizationId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.ORGANIZATION_SURVEYS, { organizationId }],
    queryFn: () => fetcher({ path: `v1/onboarding/organizations/${organizationId}/surveys/` }),
    ...options,
  });
}

export function useAddTokens() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, data }) =>
      fetcher({
        path: `v1/onboarding/organizations/${organizationId}/tokens/add/`,
        method: 'POST',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries([QueryKeys.ORGANIZATIONS]);
      queryClient.invalidateQueries([QueryKeys.ORGANIZATION]);
      queryClient.invalidateQueries([QueryKeys.ORGANIZATION_LEDGER, { organizationId }]);
    },
    onError: () => {
      message.error('Something went wrong adding tokens');
    },
  });
}

export function useDeductTokens() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, data }) =>
      fetcher({
        path: `v1/onboarding/organizations/${organizationId}/tokens/deduct/`,
        method: 'POST',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries([QueryKeys.ORGANIZATIONS]);
      queryClient.invalidateQueries([QueryKeys.ORGANIZATION]);
      queryClient.invalidateQueries([QueryKeys.ORGANIZATION_LEDGER, { organizationId }]);
    },
    onError: () => {
      message.error('Something went wrong deducting tokens');
    },
  });
}

export function useOrganizationLedger({ organizationId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.ORGANIZATION_LEDGER, { organizationId }],
    queryFn: () =>
      fetcher({
        path: `v1/onboarding/organizations/${organizationId}/ledger/`,
      }),
    ...options,
  });
}

export function useOrganizationConfigs({ organizationId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.ORGANIZATION_CONFIGS, { organizationId }],
    queryFn: () =>
      fetcher({
        path: `v1/onboarding/organizations/${organizationId}/get-configs/`,
      }),
    ...options,
  });
}

export function useSetOrganizationConfigs() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, data }) =>
      fetcher({
        path: `v1/onboarding/organizations/${organizationId}/set-configs/`,
        method: 'POST',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries([QueryKeys.ORGANIZATION_CONFIGS, { organizationId }]);
    },
    onError: () => {
      message.error('Something went wrong updating organization configs');
    },
  });
}

export function useResetOrganizationConfigs() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId }) =>
      fetcher({
        path: `v1/onboarding/organizations/${organizationId}/set-configs/`,
        method: 'DELETE',
      }),
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries([QueryKeys.ORGANIZATION_CONFIGS, { organizationId }]);
    },
    onError: () => {
      message.error('Something went wrong resetting organization configs');
    },
  });
}
